'use client'

import type { DayButton } from 'react-day-picker'
import * as React from 'react'
import { ChevronDownIcon, ChevronLeftIcon, ChevronRightIcon } from 'lucide-react'
import { DayPicker, getDefaultClassNames } from 'react-day-picker'

import cn from '@config/tailwind/cn'

import { Button, buttonVariants } from './button'

function Calendar({
  className,
  classNames,
  showOutsideDays = true,
  captionLayout = 'label',
  buttonVariant = 'ghost',
  formatters,
  components,
  ...props
}: React.ComponentProps<typeof DayPicker> & {
  buttonVariant?: React.ComponentProps<typeof Button>['variant']
}) {
  const defaultClassNames = getDefaultClassNames()

  return (
    <DayPicker
      showOutsideDays={showOutsideDays}
      className={cn(
        'ui:group/calendar ui:bg-neutral-white ui:p-2 ui:[--cell-radius:var(--radius-md)] ui:[--cell-size:--spacing(7)] ui:in-data-[slot=card-content]:bg-transparent ui:in-data-[slot=popover-content]:bg-transparent',
        String.raw`ui:rtl:**:[.rdp-button\_next>svg]:rotate-180`,
        String.raw`ui:rtl:**:[.rdp-button\_previous>svg]:rotate-180`,
        className,
      )}
      captionLayout={captionLayout}
      formatters={{
        formatMonthDropdown: (date) => date.toLocaleString('default', { month: 'short' }),
        ...formatters,
      }}
      classNames={{
        root: cn('ui:w-fit', defaultClassNames.root),
        months: cn('ui:relative ui:flex ui:flex-col ui:gap-4 ui:md:flex-row', defaultClassNames.months),
        month: cn('ui:flex ui:w-full ui:flex-col ui:gap-4', defaultClassNames.month),
        nav: cn(
          'ui:absolute ui:inset-x-0 ui:top-0 ui:flex ui:w-full ui:items-center ui:justify-between ui:gap-1',
          defaultClassNames.nav,
        ),
        button_previous: cn(
          buttonVariants({ variant: buttonVariant }),
          'ui:size-(--cell-size) ui:p-0 ui:select-none ui:aria-disabled:opacity-50',
          defaultClassNames.button_previous,
        ),
        button_next: cn(
          buttonVariants({ variant: buttonVariant }),
          'ui:size-(--cell-size) ui:p-0 ui:select-none ui:aria-disabled:opacity-50',
          defaultClassNames.button_next,
        ),
        month_caption: cn(
          'ui:flex ui:h-(--cell-size) ui:w-full ui:items-center ui:justify-center ui:px-(--cell-size)',
          defaultClassNames.month_caption,
        ),
        dropdowns: cn(
          'ui:flex ui:h-(--cell-size) ui:w-full ui:items-center ui:justify-center ui:gap-1.5 ui:text-sm ui:font-medium',
          defaultClassNames.dropdowns,
        ),
        dropdown_root: cn(
          'ui:cn-calendar-dropdown-root ui:relative ui:rounded-(--cell-radius)',
          defaultClassNames.dropdown_root,
        ),
        dropdown: cn('ui:absolute ui:inset-0 ui:bg-neutral-white ui:opacity-0', defaultClassNames.dropdown),
        caption_label: cn(
          'ui:font-medium ui:select-none',
          captionLayout === 'label'
            ? 'ui:text-sm'
            : 'ui:cn-calendar-caption-label ui:flex ui:items-center ui:gap-1 ui:rounded-(--cell-radius) ui:text-sm ui:[&>svg]:size-3.5 ui:[&>svg]:text-grey-100',
          defaultClassNames.caption_label,
        ),
        table: 'ui:w-full ui:border-collapse',
        weekdays: cn('ui:flex', defaultClassNames.weekdays),
        weekday: cn(
          'ui:flex-1 ui:rounded-(--cell-radius) ui:text-[0.8rem] ui:font-normal ui:text-grey-100 ui:select-none',
          defaultClassNames.weekday,
        ),
        week: cn('ui:mt-2 ui:flex ui:w-full', defaultClassNames.week),
        week_number_header: cn('ui:w-(--cell-size) ui:select-none', defaultClassNames.week_number_header),
        week_number: cn('ui:text-[0.8rem] ui:text-grey-100 ui:select-none', defaultClassNames.week_number),
        day: cn(
          'ui:group/day ui:relative ui:aspect-square ui:h-full ui:w-full ui:rounded-(--cell-radius) ui:p-0 ui:text-center ui:select-none ui:[&:last-child[data-selected=true]_button]:rounded-r-(--cell-radius)',
          props.showWeekNumber
            ? 'ui:[&:nth-child(2)[data-selected=true]_button]:rounded-l-(--cell-radius)'
            : 'ui:[&:first-child[data-selected=true]_button]:rounded-l-(--cell-radius)',
          defaultClassNames.day,
        ),
        range_start: cn(
          'ui:relative ui:isolate ui:-z-0 ui:rounded-l-(--cell-radius) ui:bg-blue-10 ui:after:absolute ui:after:inset-y-0 ui:after:right-0 ui:after:w-4 ui:after:bg-blue-10',
          defaultClassNames.range_start,
        ),
        range_middle: cn('ui:rounded-none', defaultClassNames.range_middle),
        range_end: cn(
          'ui:relative ui:isolate ui:-z-0 ui:rounded-r-(--cell-radius) ui:bg-blue-10 ui:after:absolute ui:after:inset-y-0 ui:after:left-0 ui:after:w-4 ui:after:bg-blue-10',
          defaultClassNames.range_end,
        ),
        today: cn(
          'ui:rounded-(--cell-radius) ui:bg-navy-10 ui:text-neutral-black ui:data-[selected=true]:rounded-none',
          defaultClassNames.today,
        ),
        outside: cn('ui:text-grey-100 ui:aria-selected:text-grey-100', defaultClassNames.outside),
        disabled: cn('ui:text-grey-100 ui:opacity-50', defaultClassNames.disabled),
        hidden: cn('ui:invisible', defaultClassNames.hidden),
        ...classNames,
      }}
      components={{
        Root: ({ className, rootRef, ...props }) => {
          return <div data-slot='calendar' ref={rootRef} className={cn(className)} {...props} />
        },
        Chevron: ({ className, orientation, ...props }) => {
          if (orientation === 'left') {
            return <ChevronLeftIcon className={cn('ui:size-4', className)} {...props} />
          }

          if (orientation === 'right') {
            return <ChevronRightIcon className={cn('ui:size-4', className)} {...props} />
          }

          return <ChevronDownIcon className={cn('ui:size-4', className)} {...props} />
        },
        DayButton: CalendarDayButton,
        WeekNumber: ({ children, ...props }) => {
          return (
            <td {...props}>
              <div className='ui:flex ui:size-(--cell-size) ui:items-center ui:justify-center ui:text-center'>
                {children}
              </div>
            </td>
          )
        },
        ...components,
      }}
      {...props}
    />
  )
}

function CalendarDayButton({ className, day, modifiers, ...props }: React.ComponentProps<typeof DayButton>) {
  const defaultClassNames = getDefaultClassNames()

  const ref = React.useRef<HTMLButtonElement>(null)
  React.useEffect(() => {
    if (modifiers.focused) ref.current?.focus()
  }, [modifiers.focused])

  return (
    <Button
      variant='ghost'
      size='icon'
      data-day={day.date.toLocaleDateString()}
      data-selected-single={
        modifiers.selected && !modifiers.range_start && !modifiers.range_end && !modifiers.range_middle
      }
      data-range-start={modifiers.range_start}
      data-range-end={modifiers.range_end}
      data-range-middle={modifiers.range_middle}
      className={cn(
        'ui:relative ui:text-neutral-black ui:isolate ui:z-10 ui:flex ui:aspect-square ui:size-auto ui:w-full ui:min-w-(--cell-size) ui:flex-col ui:gap-1 ui:border-0 ui:font-normal ui:leading-none ui:data-[selected-single=true]:bg-blue-100 ui:data-[selected-single=true]:text-neutral-white ui:data-[selected-single=true]:rounded-(--cell-radius) ui:data-[range-middle=true]:bg-blue-10 ui:data-[range-middle=true]:text-neutral-black ui:data-[range-start=true]:bg-blue-100 ui:data-[range-start=true]:text-neutral-white ui:data-[range-end=true]:bg-blue-100 ui:data-[range-end=true]:text-neutral-white ui:group-data-[focused=true]/day:relative ui:group-data-[focused=true]/day:z-10 ui:group-data-[focused=true]/day:border-blue-100 ui:group-data-[focused=true]/day:ring-[3px] ui:group-data-[focused=true]/day:ring-blue-20 ui:data-[range-end=true]:rounded-(--cell-radius) ui:data-[range-end=true]:rounded-r-(--cell-radius) ui:data-[range-middle=true]:rounded-none ui:data-[range-start=true]:rounded-(--cell-radius) ui:data-[range-start=true]:rounded-l-(--cell-radius) ui:[&>span]:text-xs ui:[&>span]:opacity-70',
        defaultClassNames.day,
        className,
      )}
      {...props}
    />
  )
}

export { Calendar, CalendarDayButton }
export type { DateRange } from 'react-day-picker'
